import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import HomePage from './pages/HomePage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DMCAPolicy from './pages/DMCAPolicy';
// Profile and PricingPage removed - not needed for now
import './App.css';

const Sidebar = lazy(() => import('./components/Sidebar'));
const FolderPage = lazy(() => import('./pages/FolderPage'));
const MyFilesPage = lazy(() => import('./pages/MyFilesPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const Profile = lazy(() => import('./pages/Profile'));
const ContactForm = lazy(() => import('./pages/ContactForm'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const Blog = lazy(() => import('./pages/Blog'));
const WeTransferAlternative = lazy(() => import('./pages/WeTransferAlternative'));
const Send10GBFiles = lazy(() => import('./pages/Send10GBFiles'));
const SendVideoFiles = lazy(() => import('./pages/SendVideoFiles'));
const BestFreeFileTransferService = lazy(() => import('./pages/BestFreeFileTransferService'));
const SendZipFilesOnline = lazy(() => import('./pages/SendZipFilesOnline'));
const SendFilesToClients = lazy(() => import('./pages/SendFilesToClients'));
const EmailAttachmentTooLarge = lazy(() => import('./pages/EmailAttachmentTooLarge'));

const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const TransactionalLandingPage = lazy(() => import('./pages/TransactionalLandingPage'));

// Initialize user ID on app load
function initializeUserId() {
  const KEY = 'fileshare_user_id';
  const ORIGINAL_KEY = 'fileshare_original_user_id';
  
  let userId = localStorage.getItem(KEY);
  
  // If no user ID, check if we have original ID
  if (!userId) {
    const originalId = localStorage.getItem(ORIGINAL_KEY);
    if (originalId) {
      // Restore from original
      localStorage.setItem(KEY, originalId);
      userId = originalId;
      console.log(`✅ Restored user ID from original: ${userId}`);
    } else {
      // Create new UUID
      const newId = `user_${crypto.randomUUID()}`;
      localStorage.setItem(KEY, newId);
      localStorage.setItem(ORIGINAL_KEY, newId);
      userId = newId;
      console.log(`🆕 Created new user ID: ${userId}`);
    }
  } else {
    console.log(`✅ User ID already exists: ${userId}`);
  }
  
  return userId;
}

function AppContainer() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const activeTheme = 'dark';
  
  // Initialize user ID on mount
  useEffect(() => {
    initializeUserId();
  }, []);

  useEffect(() => {
    document.documentElement.style.colorScheme = activeTheme;
  }, []);

  return (
    <div className="App min-h-screen" style={{ 
      backgroundColor: isAdminRoute ? '#f8f9fa' : '#0a0b0d',
      fontFamily: "'Largetransfer', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      color: isAdminRoute ? '#202124' : '#e4e4e7'
    }} data-theme={activeTheme}>
      {!isAdminRoute && (
        <Suspense fallback={null}>
          <Sidebar />
        </Suspense>
      )}
      
      {/* Main Content Area with responsive padding */}
      <div style={{
        marginLeft: '0px',
        paddingTop: isAdminRoute ? '0px' : '50px'
      }} className={isAdminRoute ? '' : 'md:ml-[220px] md:pt-0'}>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/uploads" element={<MyFilesPage />} />
            <Route path="/my-files" element={<MyFilesPage />} />
            <Route path="/files" element={<MyFilesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/dmca-policy" element={<DMCAPolicy />} />
            {/* Transactional Landing Pages (Uploader at Top) */}
            <Route path="/send-10gb-file" element={<TransactionalLandingPage configKey="/send-10gb-file" />} />
            <Route path="/send-20gb-file" element={<TransactionalLandingPage configKey="/send-20gb-file" />} />
            <Route path="/send-50gb-file" element={<TransactionalLandingPage configKey="/send-50gb-file" />} />
            <Route path="/send-large-video" element={<TransactionalLandingPage configKey="/send-large-video" />} />
            <Route path="/send-large-files-online" element={<TransactionalLandingPage configKey="/send-large-files-online" />} />
            <Route path="/share-large-folder" element={<TransactionalLandingPage configKey="/share-large-folder" />} />
            <Route path="/send-video-without-compression" element={<TransactionalLandingPage configKey="/send-video-without-compression" />} />

            {/* Blog Hub & Dynamic Posts */}
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />

            {/* Direct URLs for Top Guides */}
            <Route path="/blog/share-video-files-with-link" element={<BlogPostPage postSlug="share-video-files-with-link" />} />
            <Route path="/blog/upload-video-to-link" element={<BlogPostPage postSlug="upload-video-to-link" />} />
            <Route path="/blog/share-file-online-link" element={<BlogPostPage postSlug="share-file-online-link" />} />
            <Route path="/blog/how-to-send-large-video-files-online" element={<BlogPostPage postSlug="how-to-send-large-video-files-online" />} />
            <Route path="/blog/how-to-send-a-10gb-file-for-free" element={<BlogPostPage postSlug="how-to-send-a-10gb-file-for-free" />} />
            <Route path="/blog/how-to-send-a-20gb-video-online" element={<BlogPostPage postSlug="how-to-send-a-20gb-video-online" />} />
            <Route path="/blog/how-to-send-a-50gb-file-online" element={<BlogPostPage postSlug="how-to-send-a-50gb-file-online" />} />
            <Route path="/blog/how-to-send-large-videos-without-losing-quality" element={<BlogPostPage postSlug="how-to-send-large-videos-without-losing-quality" />} />
            <Route path="/blog/how-to-share-large-folders-online" element={<BlogPostPage postSlug="how-to-share-large-folders-online" />} />
            <Route path="/blog/how-to-send-large-files-by-email" element={<BlogPostPage postSlug="how-to-send-large-files-by-email" />} />
            <Route path="/blog/how-to-send-large-files-through-gmail" element={<BlogPostPage postSlug="how-to-send-large-files-through-gmail" />} />
            <Route path="/blog/how-to-send-large-files-from-android" element={<BlogPostPage postSlug="how-to-send-large-files-from-android" />} />
            <Route path="/blog/how-to-send-large-files-from-iphone" element={<BlogPostPage postSlug="how-to-send-large-files-from-iphone" />} />
            <Route path="/blog/how-to-send-large-video-files-to-clients" element={<BlogPostPage postSlug="how-to-send-large-video-files-to-clients" />} />
            <Route path="/blog/how-to-share-premiere-pro-projects" element={<BlogPostPage postSlug="how-to-share-premiere-pro-projects" />} />
            <Route path="/blog/how-to-share-blender-projects" element={<BlogPostPage postSlug="how-to-share-blender-projects" />} />
            <Route path="/blog/how-to-share-unreal-engine-game-builds" element={<BlogPostPage postSlug="how-to-share-unreal-engine-game-builds" />} />
            <Route path="/blog/best-wetransfer-alternatives-2026" element={<BlogPostPage postSlug="best-wetransfer-alternatives-2026" />} />

            {/* Legacy/Specific SEO routes */}
            <Route path="/share-video-files-with-link" element={<TransactionalLandingPage configKey="share-video-files-with-link" />} />
            <Route path="/upload-video-to-link" element={<TransactionalLandingPage configKey="upload-video-to-link" />} />
            <Route path="/share-file-online-link" element={<TransactionalLandingPage configKey="share-file-online-link" />} />
            <Route path="/best-free-file-transfer-service" element={<BestFreeFileTransferService />} />
            <Route path="/send-zip-files-online" element={<SendZipFilesOnline />} />
            <Route path="/email-attachment-too-large" element={<EmailAttachmentTooLarge />} />
            <Route path="/contact" element={<ContactForm />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/d/:folderId" element={<FolderPage />} />
            <Route path="/r/:folderId" element={<FolderPage />} />

            {/* Programmatic SEO Landing Pages (Matches all 71+ SEO matrix slugs) */}
            <Route path="/:slug" element={<TransactionalLandingPage />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContainer />
      </Router>
    </AuthProvider>
  );
}

export default App;
